import { DASHBOARD_API_BASE_URL, type DashboardApiResponse } from '../config/config'
import type { EnquiryRecord, EnquiryStatus, FaqRecord } from '../../types'

/**
 * Fetch customer enquiries from Django admin backend and local storage.
 * Endpoint: GET /api/admin/enquiries/
 */
export const apiGetEnquiries = async (): Promise<
  DashboardApiResponse<EnquiryRecord[]>
> => {
  let localEnquiries: EnquiryRecord[] = []
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('salzburg_enquiry_entries') : null
    if (raw) {
      localEnquiries = JSON.parse(raw)
    }
  } catch {}

  try {
    const url = `${DASHBOARD_API_BASE_URL.replace(/\/$/, '')}/api/admin/enquiries/`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const result = await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: localEnquiries.length > 0,
        message:
          result?.detail ||
          result?.message ||
          `Failed to fetch enquiries (HTTP ${response.status}).`,
        data: localEnquiries,
      }
    }

    const backendData: EnquiryRecord[] = Array.isArray(result?.data)
      ? result.data
      : Array.isArray(result)
        ? result
        : []

    // Merge backend and local enquiries seamlessly, avoiding duplicate IDs
    const seenIds = new Set(backendData.map((e) => String(e.id)))
    const merged = [...backendData]
    for (const local of localEnquiries) {
      if (!seenIds.has(String(local.id))) {
        merged.unshift(local)
        seenIds.add(String(local.id))
      }
    }

    return {
      success: true,
      message: result?.message || 'Enquiries fetched successfully',
      data: merged,
    }
  } catch {
    return {
      success: true,
      message: 'Loaded local enquiries.',
      data: localEnquiries,
    }
  }
}

/**
 * Update the status of an enquiry.
 * Endpoint: PATCH /api/admin/enquiries/{id}/
 */
export const apiUpdateEnquiryStatus = async (
  id: string,
  status: EnquiryStatus,
): Promise<DashboardApiResponse<EnquiryRecord>> => {
  // Sync to local storage
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('salzburg_enquiry_entries') : null
    if (raw) {
      const list = JSON.parse(raw).map((e: any) =>
        String(e.id) === String(id) ? { ...e, status } : e
      )
      localStorage.setItem('salzburg_enquiry_entries', JSON.stringify(list))
    }
  } catch {}

  try {
    const url = `${DASHBOARD_API_BASE_URL.replace(/\/$/, '')}/api/admin/enquiries/${id}/`

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    })

    const result = await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.detail ||
          result?.message ||
          'Failed to update enquiry status.',
      }
    }

    const data = result?.data ?? result

    return {
      success: true,
      message: result?.message || 'Status updated successfully.',
      data,
    }
  } catch {
    return {
      success: true,
      message: 'Status updated locally.',
      data: { id, status } as any,
    }
  }
}

/**
 * Delete an enquiry.
 * Endpoint: DELETE /api/admin/enquiries/{id}/
 */
export const apiDeleteEnquiry = async (
  id: string,
): Promise<DashboardApiResponse<void>> => {
  // Sync to local storage
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('salzburg_enquiry_entries') : null
    if (raw) {
      const list = JSON.parse(raw).filter((e: any) => String(e.id) !== String(id))
      localStorage.setItem('salzburg_enquiry_entries', JSON.stringify(list))
    }
  } catch {}

  try {
    const url = `${DASHBOARD_API_BASE_URL.replace(/\/$/, '')}/api/admin/enquiries/${id}/`

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const result = await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.detail ||
          result?.message ||
          'Failed to delete enquiry.',
      }
    }

    return {
      success: true,
      message: result?.message || 'Enquiry deleted successfully.',
    }
  } catch {
    return {
      success: true,
      message: 'Enquiry deleted locally.',
    }
  }
}

const normalizeFaqRecord = (item: any): FaqRecord => {
  return {
    id: String(item.id ?? ''),
    question: String(item.question ?? ''),
    answer: String(item.answer ?? ''),
    category: String(item.category ?? 'General'),
  }
}

/**
 * Fetch FAQs from the Django admin backend.
 * Endpoint: GET /api/admin/faqs/
 */
export const apiGetFaqs = async (): Promise<
  DashboardApiResponse<FaqRecord[]>
> => {
  try {
    const url = `${DASHBOARD_API_BASE_URL.replace(/\/$/, '')}/api/admin/faqs/`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const result = await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.detail ||
          result?.message ||
          `Failed to fetch FAQs (HTTP ${response.status}).`,
        data: [],
      }
    }

    const rawList = Array.isArray(result?.data)
      ? result.data
      : Array.isArray(result)
        ? result
        : []

    const data = rawList.map(normalizeFaqRecord)

    return {
      success: true,
      message: result?.message || 'FAQs fetched successfully',
      data,
    }
  } catch {
    return {
      success: false,
      message: 'Connection failed.',
      data: [],
    }
  }
}
